import { useGridContext } from 'admin/src/components/ui/DataGrid/DataGridContext';
import { useDataGridPersistedState } from 'admin/src/components/ui/DataGrid/useDataGridPersistedState';
import { standardDataGridDateComparator } from 'admin/src/components/ui/DataGrid/utils';
import LoadingOverlay from 'admin/src/components/ui/LoadingOverlay';
import type { ShellContextProps } from 'admin/src/components/ui/Shell';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  type ColGroupDef,
  ColumnAutoSizeModule,
  ExcelExportModule,
  type GridReadyEvent,
  GridStateModule,
  ModuleRegistry,
  MultiFilterModule,
  RowGroupingModule,
  type RowSelectionOptions,
  SetFilterModule,
  type SizeColumnsToFitGridStrategy,
  themeQuartz,
} from 'ag-grid-enterprise';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { throttle } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { cn } from 'shared/src/utils';

import 'admin/src/components/ui/DataGrid/DataGrid.css';

ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  SetFilterModule,
  MultiFilterModule,
  RowGroupingModule,
  ExcelExportModule,
  GridStateModule,
]);

const easyVoteTheme = themeQuartz.withParams({
  fontFamily: 'Open Sans, sans-serif',
  fontSize: '12px',
});

const autoSizeStrategy: SizeColumnsToFitGridStrategy = { type: 'fitGridWidth' };
const rowSelection: RowSelectionOptions = { mode: 'multiRow', enableClickSelection: true, selectAll: 'filtered' };
const _defaultColDef = {
  suppressHeaderMenuButton: true,
  resizable: false,
  minWidth: 150,
};

export interface DataGridProps<TData> {
  rowData?: TData[];
  loading: boolean;
  className?: string;
  loadingMessage?: string;
  persistGridState?: boolean;
  scopeKey?: string;
  columnDefsReady?: boolean;
  gridId: string;
  getRowId?: AgGridReactProps['getRowId'];
  columnDefs: AgGridReactProps['columnDefs'];
  rowSelection?: AgGridReactProps['rowSelection'];
  defaultColDef?: AgGridReactProps['defaultColDef'];
  loadingOverlayComponentsParams?: AgGridReactProps['loadingOverlayComponentParams'];
  loadingOverlayComponent?: AgGridReactProps['loadingOverlayComponent'];
  rowHeight?: AgGridReactProps['rowHeight'];
  autoSizeStrategy?: AgGridReactProps['autoSizeStrategy'];
  groupDisplayType?: AgGridReactProps['groupDisplayType'];
  groupDefaultExpanded?: AgGridReactProps['groupDefaultExpanded'];
  autoGroupColumnDef?: AgGridReactProps['autoGroupColumnDef'];
  onColumnVisible?: AgGridReactProps['onColumnVisible'];
  onDisplayedColumnsChanged?: AgGridReactProps['onDisplayedColumnsChanged'];
  onGridReady?: AgGridReactProps['onGridReady'];
  onSelectionChanged?: AgGridReactProps['onSelectionChanged'];
  onCellValueChanged?: AgGridReactProps['onCellValueChanged'];
  overlayNoRowsTemplate?: AgGridReactProps['overlayNoRowsTemplate'];
  noRowsOverlayComponent?: AgGridReactProps['noRowsOverlayComponent'];
  noRowsOverlayComponentParams?: AgGridReactProps['noRowsOverlayComponentParams'];
  onColumnMoved?: AgGridReactProps['onColumnMoved'];
  context?: AgGridReactProps['context'];
  theme?: ShellContextProps['theme'];
}

export function DataGrid<TData>({
  className,
  columnDefs,
  persistGridState = true,
  scopeKey,
  columnDefsReady: columnDefsReadyProp,
  loading,
  loadingMessage = 'Loading...',
  rowSelection: customRowSelection,
  theme,
  gridId,
  ...props
}: DataGridProps<TData>) {
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const { agGridRef, setGridApi, gridApi, setLoading } = useGridContext();

  const correctedColDefs = useMemo(() => {
    if (!columnDefs) return [];

    const correctColDef = (colDef: ColDef | ColGroupDef): ColDef | ColGroupDef => {
      const group = colDef as ColGroupDef;
      if (group.children?.length) {
        return {
          ...group,
          children: group.children.map((child) => correctColDef(child as ColDef | ColGroupDef)),
        };
      }

      const leaf = colDef as ColDef;
      const cellDataType = leaf.cellDataType === true ? 'boolean' : leaf.cellDataType;
      const isDateColumn = cellDataType === 'date' || cellDataType === 'dateString' || cellDataType === 'dateTime' || cellDataType === 'dateTimeString';
      const usesDateFilter = leaf.filter === 'agDateColumnFilter' || (isDateColumn && !!leaf.filter);

      if (usesDateFilter) {
        return {
          ...leaf,
          // Workers (and others) use filter: true with cellDataType: 'date'. Coerce so setColumnFilterModel
          // receives a real date filter instance and our day-level comparator is attached.
          filter: 'agDateColumnFilter',
          filterParams: {
            ...(leaf.filterParams || {}),
            comparator: leaf.filterParams?.comparator || standardDataGridDateComparator,
          },
        };
      }

      if (leaf.cellDataType === 'boolean' && !leaf.filterParams?.values?.length) {
        return {
          ...leaf,
          filterParams: {
            ...(leaf.filterParams || {}),
            values: ['false', 'true', null], // order matters
          },
        };
      }

      const isBooleanColumn = cellDataType === 'boolean';
      const usesTextFilter =
        !!leaf.filter && !usesDateFilter && !isBooleanColumn && (leaf.filter === true || leaf.filter === 'agSetColumnFilter' || leaf.filter === 'agTextColumnFilter');

      if (usesTextFilter) {
        return {
          ...leaf,
          filter: 'agTextColumnFilter',
          filterParams: {
            ...(leaf.filterParams || {}),
            filterOptions: leaf.filterParams?.filterOptions ?? ['equals', 'notEqual', 'blank', 'notBlank'],
            maxNumConditions: leaf.filterParams?.maxNumConditions ?? 100,
            numAlwaysVisibleConditions: leaf.filterParams?.numAlwaysVisibleConditions ?? 1,
          },
        };
      }

      return leaf;
    };

    return columnDefs.map((colDef) => correctColDef(colDef as ColDef | ColGroupDef));
  }, [columnDefs]);

  const columnDefsReady = columnDefsReadyProp ?? correctedColDefs.length > 0;
  const { initialState, onStateUpdated, readyToMount, gridInstanceKey } = useDataGridPersistedState(gridId, {
    persistState: persistGridState,
    columnDefsReady,
    dataReady: !loading,
    scopeKey,
  });

  useEffect(() => {
    setLoading(loading);
    return () => setLoading(false);
  }, [loading, setLoading]);

  useEffect(() => {
    if (!gridApi || !gridWrapperRef.current) return;

    const wrapper = gridWrapperRef.current;
    const onResize = throttle(() => gridApi?.sizeColumnsToFit(), 250);
    const resizeObserver = new ResizeObserver(onResize);

    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.unobserve(wrapper);
    };
  }, [gridApi]);

  const defaultColDef = useMemo(
    () => ({
      ..._defaultColDef,
      ...(props.defaultColDef || {}),
    }),
    [props.defaultColDef],
  );

  useEffect(() => {
    return () => {
      setGridApi(undefined);
    };
  }, [setGridApi]);

  useEffect(() => {
    void gridInstanceKey;
    setGridApi(undefined);
  }, [gridInstanceKey, setGridApi]);

  useEffect(() => {
    if (persistGridState && !readyToMount) {
      setGridApi(undefined);
    }
  }, [persistGridState, readyToMount, setGridApi]);

  function onGridReady(event: GridReadyEvent) {
    setGridApi(event.api);
    props.onGridReady?.(event);
  }

  return (
    <div
      ref={gridWrapperRef}
      data-testid={gridApi?.getGridId() ? `${gridApi.getGridId()}-ready` : ''}
      className={cn(`theme-${theme} ag-theme-quartz h-full border-none`, className)}
    >
      {persistGridState && !readyToMount ? (
        <div className="h-full flex items-center justify-center min-h-0">
          <LoadingOverlay infoText={loadingMessage} animate isPreview={false} />
        </div>
      ) : (
        <AgGridReact
          key={gridInstanceKey}
          rowSelection={customRowSelection || rowSelection}
          loading={loading}
          loadingOverlayComponentParams={{ animate: true, isPreview: false, infoText: loadingMessage }}
          loadingOverlayComponent={LoadingOverlay}
          rowHeight={40}
          autoSizeStrategy={autoSizeStrategy}
          suppressContextMenu
          maintainColumnOrder
          overlayNoRowsTemplate="No data to show"
          selectionColumnDef={{
            pinned: 'left',
          }}
          {...(props as AgGridReactProps)}
          gridId={gridId}
          columnDefs={correctedColDefs}
          initialState={initialState}
          onStateUpdated={onStateUpdated}
          onGridReady={onGridReady}
          ref={agGridRef}
          theme={easyVoteTheme}
          defaultColDef={defaultColDef}
          onColumnVisible={props.onColumnVisible}
          onDisplayedColumnsChanged={props.onDisplayedColumnsChanged}
        />
      )}
    </div>
  );
}
